import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  EnrichedEntity,
  EnrichmentHooks,
  useDeleteEnrichedEntityMutation,
  useGetEnrichedEntitiesQuery,
  useGetEnrichmentFacetsQuery,
  useUpdateEnrichedEntityMutation,
} from '../redux/adminApiSlice';
import { gridTheme, gridTokens } from '../theme/gridTheme';

const PAGE_SIZE = 50;

function formatDate(value?: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return String(value);
  }
}

function truncate(s: string | undefined, n: number): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

/** Multi-line text ⇄ string[] (one item per line). */
function linesToArray(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}
function arrayToLines(arr?: string[]): string {
  return (arr || []).join('\n');
}

const statusColor = (s: string): 'success' | 'default' =>
  s === 'resolved' ? 'success' : 'default';

// ─── Edit dialog draft (arrays edited as multi-line text) ──────────────────────

interface Draft {
  valueDisplay: string;
  status: string;
  source: string;
  reviewed: boolean;
  facts: string; // one per line
  descriptor: string;
  year: string;
  externalId: string;
  genres: string; // one per line
  peopleText: string; // "name | role" per line
  // hooks (all one-per-line)
  catchphrases: string;
  quotableLines: string;
  signatureLyrics: string;
  personaTag: string;
  eras: string; // one per line
  openers1v1: string; // one per line
  openersRoom: string; // one per line
}

function toDraft(e: EnrichedEntity): Draft {
  const h = e.hooks || {};
  return {
    valueDisplay: e.valueDisplay || '',
    status: e.status || 'unresolved',
    source: e.source || 'none',
    reviewed: Boolean(e.reviewed),
    facts: arrayToLines(e.facts),
    descriptor: e.descriptor || '',
    year: e.year != null ? String(e.year) : '',
    externalId: e.externalId || '',
    genres: arrayToLines(e.genres),
    peopleText: (e.people || [])
      .map((p) => (p.role ? `${p.name} | ${p.role}` : p.name))
      .join('\n'),
    catchphrases: arrayToLines(h.catchphrases),
    quotableLines: arrayToLines(h.quotableLines),
    signatureLyrics: arrayToLines(h.signatureLyrics),
    personaTag: h.personaTag || '',
    eras: arrayToLines(h.eras),
    openers1v1: arrayToLines(h.openers1v1),
    openersRoom: arrayToLines(h.openersRoom),
  };
}

function draftToUpdates(d: Draft): Partial<EnrichedEntity> {
  const hooks: EnrichmentHooks = {};
  const catchphrases = linesToArray(d.catchphrases);
  if (catchphrases.length) hooks.catchphrases = catchphrases;
  const quotableLines = linesToArray(d.quotableLines);
  if (quotableLines.length) hooks.quotableLines = quotableLines;
  const signatureLyrics = linesToArray(d.signatureLyrics);
  if (signatureLyrics.length) hooks.signatureLyrics = signatureLyrics;
  if (d.personaTag.trim()) hooks.personaTag = d.personaTag.trim();
  const eras = linesToArray(d.eras);
  if (eras.length) hooks.eras = eras;
  const o1 = linesToArray(d.openers1v1);
  if (o1.length) hooks.openers1v1 = o1;
  const oR = linesToArray(d.openersRoom);
  if (oR.length) hooks.openersRoom = oR;

  const people = linesToArray(d.peopleText).map((line) => {
    const [name, role] = line.split('|').map((s) => s.trim());
    return role ? { name, role } : { name };
  });

  const yearNum = d.year.trim() ? Number(d.year.trim()) : null;

  return {
    valueDisplay: d.valueDisplay.trim(),
    status: d.status as EnrichedEntity['status'],
    source: d.source,
    reviewed: d.reviewed,
    facts: linesToArray(d.facts),
    descriptor: d.descriptor.trim() || undefined,
    year: Number.isFinite(yearNum as number) ? (yearNum as number) : undefined,
    externalId: d.externalId.trim() || undefined,
    genres: linesToArray(d.genres),
    people,
    hooks: Object.keys(hooks).length ? hooks : undefined,
  };
}

const multilineProps = {
  multiline: true,
  minRows: 2,
  maxRows: 8,
  fullWidth: true,
  size: 'small' as const,
};

// ─── Page ──────────────────────────────────────────────────────────────────

const EnrichmentPage: React.FC = () => {
  const isMobile = useMediaQuery(gridTheme.breakpoints.down('sm'));
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [hasHooks, setHasHooks] = useState(false);
  const [reviewedFilter, setReviewedFilter] = useState<'' | 'reviewed' | 'unreviewed'>('');
  const [sort, setSort] = useState('updatedAt');
  const [page, setPage] = useState(1);

  const [editing, setEditing] = useState<EnrichedEntity | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const facets = useGetEnrichmentFacetsQuery();
  const listQuery = useGetEnrichedEntitiesQuery({
    search: debouncedSearch || undefined,
    kind: kind || undefined,
    status: status || undefined,
    source: source || undefined,
    hasHooks: hasHooks || undefined,
    reviewed:
      reviewedFilter === 'reviewed'
        ? true
        : reviewedFilter === 'unreviewed'
          ? false
          : undefined,
    sort,
    limit: PAGE_SIZE,
    skip: (page - 1) * PAGE_SIZE,
  });

  const [updateEntity, updateState] = useUpdateEnrichedEntityMutation();
  const [deleteEntity] = useDeleteEnrichedEntityMutation();

  const items = listQuery.data?.items || [];
  const total = listQuery.data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const kinds = facets.data?.kinds || [];
  const sources = facets.data?.sources || [];
  const statuses = facets.data?.statuses || [];

  const openEdit = (e: EnrichedEntity) => {
    setEditing(e);
    setDraft(toDraft(e));
    setError(null);
  };
  const closeEdit = () => {
    setEditing(null);
    setDraft(null);
  };

  const patch = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  const onSave = async () => {
    if (!editing || !draft) return;
    setError(null);
    try {
      await updateEntity({ id: editing._id, updates: draftToUpdates(draft) }).unwrap();
      setMessage(`Saved “${draft.valueDisplay}”`);
      closeEdit();
    } catch (e) {
      setError((e as { data?: { message?: string } })?.data?.message || 'Save failed');
    }
  };

  const onDelete = async (e: EnrichedEntity) => {
    if (!window.confirm(`Delete enrichment for “${e.valueDisplay}” (${e.kind})? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteEntity(e._id).unwrap();
      setMessage(`Deleted “${e.valueDisplay}”`);
    } catch {
      setError('Delete failed');
    }
  };

  const hookCount = (e: EnrichedEntity): number => {
    const h = e.hooks;
    if (!h) return 0;
    let c = 0;
    c += (h.catchphrases?.length || 0);
    c += (h.quotableLines?.length || 0);
    c += (h.signatureLyrics?.length || 0);
    if (h.personaTag) c++;
    c += (h.eras?.length || 0);
    c += (h.openers1v1?.length || 0);
    c += (h.openersRoom?.length || 0);
    return c;
  };

  const filtersActive = useMemo(
    () => Boolean(debouncedSearch || kind || status || source || hasHooks || reviewedFilter),
    [debouncedSearch, kind, status, source, hasHooks, reviewedFilter]
  );

  return (
    <ThemeProvider theme={gridTheme}>
      <Box
        sx={{
          p: { xs: 1.5, sm: 3 },
          minHeight: '100vh',
          bgcolor: gridTokens.bgPrimary,
          color: gridTokens.textPrimary,
        }}
      >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AutoFixHighIcon color="primary" />
        <Typography variant="h4">Enrichment</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The riff material behind “In Common” openers &amp; room headlines. Each row is a thing people
        put in their profile (a show, artist, game…). Edit the <strong>hooks</strong> to change the
        jokes the app writes. Curated seed rows live in <code>enrichmentSeed.ts</code>; edits here
        write straight to the DB.
      </Typography>

      {message ? (
        <Alert severity="success" onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message}
        </Alert>
      ) : null}
      {error && !editing ? (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {/* Filters — each control is full-width on phones, inline on desktop. */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <TextField
          label="Search"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="value or fact…"
          sx={{ minWidth: { xs: '100%', sm: 220 } }}
        />
        <FormControl size="small" sx={{ minWidth: { xs: '47%', sm: 130 }, flexGrow: { xs: 1, sm: 0 } }}>
          <InputLabel>Kind</InputLabel>
          <Select label="Kind" value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            {kinds.map((k) => (
              <MenuItem key={k} value={k}>{k}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: '47%', sm: 140 }, flexGrow: { xs: 1, sm: 0 } }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            {statuses.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: '47%', sm: 140 }, flexGrow: { xs: 1, sm: 0 } }}>
          <InputLabel>Source</InputLabel>
          <Select label="Source" value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            {sources.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: '47%', sm: 160 }, flexGrow: { xs: 1, sm: 0 } }}>
          <InputLabel>Reviewed</InputLabel>
          <Select
            label="Reviewed"
            value={reviewedFilter}
            onChange={(e) => {
              setReviewedFilter(e.target.value as '' | 'reviewed' | 'unreviewed');
              setPage(1);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="reviewed">Reviewed ✓</MenuItem>
            <MenuItem value="unreviewed">Needs review</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: '47%', sm: 150 }, flexGrow: { xs: 1, sm: 0 } }}>
          <InputLabel>Sort</InputLabel>
          <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
            <MenuItem value="updatedAt">Recently updated</MenuItem>
            <MenuItem value="valueDisplay">Name (A–Z)</MenuItem>
            <MenuItem value="kind">Kind</MenuItem>
            <MenuItem value="fetchedAt">Recently fetched</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={hasHooks} onChange={(e) => { setHasHooks(e.target.checked); setPage(1); }} />}
          label="Has hooks"
        />
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {listQuery.isFetching ? 'Loading…' : `${total} ${filtersActive ? 'matching' : 'total'} entities`}
      </Typography>

      {/* Mobile: tappable cards (the wide table can't fit a phone). */}
      {isMobile ? (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {listQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : items.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              No enrichment entities match.
            </Typography>
          ) : (
            items.map((e) => {
              const hooks = hookCount(e);
              return (
                <Box
                  key={e._id}
                  onClick={() => openEdit(e)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    bgcolor: gridTokens.bgSecondary,
                    cursor: 'pointer',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography sx={{ fontWeight: 700 }} noWrap>
                      {e.valueDisplay}
                    </Typography>
                    <Chip size="small" label={e.kind} variant="outlined" />
                  </Stack>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
                    <Chip size="small" label={e.status} color={statusColor(e.status)} />
                    <Chip size="small" variant="outlined" label={`src: ${e.source}`} />
                    {hooks > 0 ? <Chip size="small" color="primary" label={`${hooks} hooks`} /> : null}
                    {e.reviewed ? <Chip size="small" color="success" label="reviewed ✓" /> : null}
                  </Stack>
                  {e.facts?.[0] ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {truncate(e.facts[0], 120)}
                      {e.facts.length > 1 ? ` (+${e.facts.length - 1})` : ''}
                    </Typography>
                  ) : null}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} onClick={(ev) => ev.stopPropagation()}>
                    <Button size="small" variant="outlined" onClick={() => openEdit(e)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => onDelete(e)}>
                      Delete
                    </Button>
                  </Stack>
                </Box>
              );
            })
          )}
        </Stack>
      ) : (
      /* Desktop: full table */
      <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Value</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Hooks</TableCell>
              <TableCell>Reviewed</TableCell>
              <TableCell>Fact</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listQuery.isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No enrichment entities match.
                </TableCell>
              </TableRow>
            ) : (
              items.map((e) => {
                const hooks = hookCount(e);
                return (
                  <TableRow key={e._id} hover sx={{ cursor: 'pointer' }} onClick={() => openEdit(e)}>
                    <TableCell sx={{ fontWeight: 600 }}>{e.valueDisplay}</TableCell>
                    <TableCell>
                      <Chip size="small" label={e.kind} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={e.status} color={statusColor(e.status)} />
                    </TableCell>
                    <TableCell>{e.source}</TableCell>
                    <TableCell>
                      {hooks > 0 ? (
                        <Chip size="small" color="primary" label={hooks} />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {e.reviewed ? (
                        <Chip size="small" color="success" label="✓" />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Tooltip title={(e.facts || []).join('\n')}>
                        <span>
                          {truncate(e.facts?.[0], 60) || '—'}
                          {e.facts && e.facts.length > 1 ? ` (+${e.facts.length - 1})` : ''}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatDate(e.updatedAt)}</TableCell>
                    <TableCell align="right" onClick={(ev) => ev.stopPropagation()}>
                      <Button size="small" onClick={() => openEdit(e)}>Edit</Button>
                      <Button size="small" color="error" onClick={() => onDelete(e)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
      )}

      {pageCount > 1 ? (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination count={pageCount} page={page} onChange={(_e, p) => setPage(p)} color="primary" />
        </Stack>
      ) : null}

      {/* Edit dialog */}
      <Dialog open={!!editing} onClose={closeEdit} maxWidth="md" fullWidth fullScreen={isMobile}>
        {editing && draft ? (
          <>
            <DialogTitle>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1}
              >
                <span>
                  Edit “{editing.valueDisplay}”
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {editing.kind} · key: {editing.valueKey}
                  </Typography>
                </span>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Switch
                      color="success"
                      checked={draft.reviewed}
                      onChange={(e) => patch('reviewed', e.target.checked)}
                    />
                  }
                  label="Human-checked"
                />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              ) : null}

              <Typography variant="overline" color="text.secondary">Facts</Typography>
              <Grid container spacing={2} sx={{ mb: 1, mt: 0 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Display value"
                    size="small"
                    fullWidth
                    value={draft.valueDisplay}
                    onChange={(e) => patch('valueDisplay', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={draft.status} onChange={(e) => patch('status', e.target.value)}>
                      {statuses.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Source</InputLabel>
                    <Select label="Source" value={draft.source} onChange={(e) => patch('source', e.target.value)}>
                      {sources.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Facts — one per line, press Enter for more"
                    {...multilineProps}
                    value={draft.facts}
                    onChange={(e) => patch('facts', e.target.value)}
                    helperText="e.g. “A medical drama led by Hugh Laurie.” Add several; the copy engine varies among them."
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Descriptor"
                    size="small"
                    fullWidth
                    value={draft.descriptor}
                    onChange={(e) => patch('descriptor', e.target.value)}
                    placeholder="TV series, Film, Album…"
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Year"
                    size="small"
                    fullWidth
                    value={draft.year}
                    onChange={(e) => patch('year', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="External ID"
                    size="small"
                    fullWidth
                    value={draft.externalId}
                    onChange={(e) => patch('externalId', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Genres (one per line)"
                    {...multilineProps}
                    value={draft.genres}
                    onChange={(e) => patch('genres', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="People (name | role, one per line)"
                    {...multilineProps}
                    value={draft.peopleText}
                    onChange={(e) => patch('peopleText', e.target.value)}
                    helperText="e.g. Hugh Laurie | lead"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary">
                Hooks — the riff material openers compose from
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Each field below is a list — put <strong>one entry per line</strong> (press
                Enter to add another). The copy engine picks a different one each time, so
                add as many variations as you like.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Catchphrases — one per line, press Enter for more"
                    {...multilineProps}
                    value={draft.catchphrases}
                    onChange={(e) => patch('catchphrases', e.target.value)}
                    helperText="Lines FROM the thing (“Bears. Beets.”). Add as many as you like."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Quotable lines — one per line, press Enter for more"
                    {...multilineProps}
                    value={draft.quotableLines}
                    onChange={(e) => patch('quotableLines', e.target.value)}
                    helperText="Lines said TO a person (“you’re my only hope”). Add as many as you like."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Signature lyrics — one per line, press Enter for more"
                    {...multilineProps}
                    value={draft.signatureLyrics}
                    onChange={(e) => patch('signatureLyrics', e.target.value)}
                    helperText="Recognizable lyric fragments. Add as many as you like."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Persona tag"
                    size="small"
                    fullWidth
                    value={draft.personaTag}
                    onChange={(e) => patch('personaTag', e.target.value)}
                    placeholder="nightlife, cozy, chaotic…"
                    helperText="A coarse one-word ‘vibe’ label for this thing. NOT shown to users and not yet wired into any copy — it’s reserved for future trait-combination openers (e.g. group everyone with a ‘nightlife’ persona). Safe to leave blank."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Eras / albums (one per line)"
                    {...multilineProps}
                    value={draft.eras}
                    onChange={(e) => patch('eras', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="1-1 openers — one per line, press Enter for more"
                    {...multilineProps}
                    value={draft.openers1v1}
                    onChange={(e) => patch('openers1v1', e.target.value)}
                    helperText="Placeholders: {name}, {value}"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Room openers — one per line, press Enter for more"
                    {...multilineProps}
                    value={draft.openersRoom}
                    onChange={(e) => patch('openersRoom', e.target.value)}
                    helperText="Placeholders: {People}, {n}, {value}"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeEdit}>Cancel</Button>
              <Button variant="contained" onClick={onSave} disabled={updateState.isLoading}>
                {updateState.isLoading ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default EnrichmentPage;
